import type { Locale } from '../_lib/marketing-routes';

/**
 * Chrome-Texte (Header + Footer) pro Sprache. Reine Strings (keine ReactNode).
 * Hrefs baut das jeweilige Component aus der Slug-Map; hier NUR Labels.
 * `Record<Locale, ChromeContent>` erzwingt Parität (fehlt ein Feld → Typfehler).
 *
 * Hinweis: Auf EN/FR-Seiten zeigen Links zu NICHT übersetzten Zielen (Blog,
 * Karriere, Presse, Changelog, Legal) bewusst auf die DE-URL – das ist alles,
 * was existiert (EN/FR = Reichweiten-Vorbereitung, kein Vollausbau). Labels sind
 * dennoch lokalisiert. Übersetzte Ziele nutzen die Locale-Slugs.
 */
export interface ChromeContent {
  brandAria: string;
  openMenu: string;
  nav: {
    service: string;
    warumLocari: string;
    antiProfiling: string;
    whatsapp: string;
    preise: string;
    faq: string;
  };
  auth: { signIn: string; signUp: string; toApp: string };
  footer: {
    tagline: string;
    newsletterAria: string;
    newsletterEmailLabel: string;
    newsletterPlaceholder: string;
    newsletterButton: string;
    /**
     * Art.-13-Kurzhinweis am Erfassungspunkt (DOI + jederzeitiger Widerruf).
     * Wortlaut mit `consent-texts.ts` (doiNotice) abgeglichen; hier client-safe
     * dupliziert, weil `consent-texts.ts` `server-only` ist und der Footer ein
     * Client-Component ist. Der Datenschutz-Link wird vom Footer angehängt.
     */
    newsletterNote: string;
    /** Neutrale Erfolgsmeldung nach dem Footer-Submit (kein Enumeration-Orakel). */
    newsletterSuccess: string;
    privacyLabel: string;
    colProduct: string;
    colCompany: string;
    colLegal: string;
    colContact: string;
    /**
     * Footer-Label (Produkt-Spalte) für den /funktionen-Use-Case-Index.
     * Rendert nur in DE (DE-only-Seite, Blog-Präzedenz) – die EN/FR-Labels
     * erfüllen die Record-Parität.
     */
    funktionen: string;
    /** Footer-Label (Produkt-Spalte) für die /whatsapp-Produktseite. */
    whatsapp: string;
    /** Produkt-Spalte: KI- und Sicherheits-Seite. */
    ki: string;
    sicherheit: string;
    ueberUns: string;
    blog: string;
    changelog: string;
    karriere: string;
    presse: string;
    trustCenter: string;
    impressum: string;
    cookieRichtlinie: string;
    cookieSettings: string;
    widerruf: string;
    dsaMeldung: string;
    kontaktAufnehmen: string;
    supportHilfe: string;
    datenschutzKontakt: string;
    statusLabel: string;
    socialAria: string;
    /** Folgt auf "© {year} " */
    copyrightSuffix: string;
    /**
     * Anschrift in der Fusszeile. 🔴 Bis zum 13.08.2026 stand die Anschrift NUR auf
     * `/legal/impressum` — Crawler und KI-Dienste, die nur die Startseite lesen, fanden sie nicht.
     * Genau in diese Luecke hat Perplexity eine erfundene Berliner Adresse gesetzt. Strasse und Ort
     * sind sprachneutral, nur das Land wird uebersetzt.
     */
    addressLine: string;
    langAria: string;
  };
}

/**
 * Chrome-Texte fürs zentrale Buchungs-Overlay (Microsoft Bookings, PR-4).
 * `Record<Locale, BookingModalContent>` erzwingt DE/EN/FR-Parität.
 * `title` je Buchungs-Kontext (messe = Slot am Stand, demo = Demo).
 */
export interface BookingModalContent {
  title: { messe: string; demo: string };
  /** aria-label des Schließen-Buttons. */
  close: string;
  /** iframe title (a11y). */
  iframeTitle: string;
  /** Sichtbarer Fallback-Link, falls Framing tenant-seitig blockiert. */
  fallback: string;
  /** Hinweis-Band, NUR im Messe-Kontext (Termine nur Fr 31.10. & Sa 01.11.2026). */
  messeNotice: string;
}

export const bookingModalContent: Record<Locale, BookingModalContent> = {
  de: {
    title: {
      messe: 'Slot am Stand buchen',
      demo: 'Demo buchen',
    },
    close: 'Schließen',
    iframeTitle: 'Terminbuchung',
    fallback: 'In neuem Tab öffnen',
    messeNotice:
      'Buchbar nur Fr 31.10. & Sa 01.11.2026 – im Kalender auf Oktober/November blättern.',
  },
  en: {
    title: {
      messe: 'Book a slot at the booth',
      demo: 'Book a demo',
    },
    close: 'Close',
    iframeTitle: 'Appointment booking',
    fallback: 'Open in new tab',
    messeNotice:
      'Available only Fri 31 Oct & Sat 1 Nov 2026 – page through to October/November in the calendar.',
  },
  fr: {
    title: {
      messe: 'Réserver un créneau au stand',
      demo: 'Réserver une démo',
    },
    close: 'Fermer',
    iframeTitle: 'Prise de rendez-vous',
    fallback: 'Ouvrir dans un nouvel onglet',
    messeNotice:
      'Disponible uniquement ven. 31.10 & sam. 01.11.2026 – naviguez jusqu’à octobre/novembre dans le calendrier.',
  },
};

export const chromeContent: Record<Locale, ChromeContent> = {
  de: {
    brandAria: 'Locari Home',
    openMenu: 'Menü öffnen',
    nav: {
      service: 'Wie es funktioniert',
      warumLocari: 'Warum Locari',
      antiProfiling: 'Anti-Profiling',
      whatsapp: 'WhatsApp',
      preise: 'Preise',
      faq: 'FAQ',
    },
    auth: {
      signIn: 'Anmelden',
      signUp: 'Kostenlos starten',
      toApp: 'Zur Plattform',
    },
    footer: {
      tagline:
        'Persönlicher Vermietungs-Assistent. Locari arbeitet – du entscheidest.',
      newsletterAria: 'Newsletter abonnieren',
      newsletterEmailLabel: 'E-Mail-Adresse',
      newsletterPlaceholder: 'deine@email.de',
      newsletterButton: 'Abonnieren',
      newsletterNote: 'Weitere Infos in unserer',
      newsletterSuccess:
        'Fast geschafft! Falls du noch nicht angemeldet bist, ist eine Bestätigungs-Mail unterwegs.',
      privacyLabel: 'Datenschutzerklärung',
      colProduct: 'Produkt',
      colCompany: 'Unternehmen',
      colLegal: 'Rechtliches',
      colContact: 'Kontakt',
      funktionen: 'Alle Funktionen',
      whatsapp: 'Steuerung per WhatsApp',
      ki: 'Künstliche Intelligenz',
      sicherheit: 'Sicherheit',
      ueberUns: 'Über uns',
      blog: 'Blog',
      changelog: 'Changelog',
      karriere: 'Karriere',
      presse: 'Presse',
      trustCenter: 'Trust Center',
      impressum: 'Impressum',
      cookieRichtlinie: 'Cookie-Richtlinie',
      cookieSettings: 'Cookie-Einstellungen',
      widerruf: 'Widerruf & Kündigung',
      dsaMeldung: 'Meldung rechtswidriger Inhalte',
      kontaktAufnehmen: 'Kontakt aufnehmen',
      supportHilfe: 'Support & Hilfe',
      datenschutzKontakt: 'Datenschutz',
      statusLabel: 'Systemstatus',
      socialAria: 'Locari auf Social Media',
      copyrightSuffix: 'Locari. Ein Service der Ametis Digital GmbH.',
      addressLine: 'Rathausgasse 17 · 12529 Schönefeld · Deutschland',
      langAria: 'Sprache wählen',
    },
  },
  en: {
    brandAria: 'Locari home',
    openMenu: 'Open menu',
    nav: {
      service: 'How it works',
      warumLocari: 'Why Locari',
      antiProfiling: 'Anti-profiling',
      whatsapp: 'WhatsApp',
      preise: 'Pricing',
      faq: 'FAQ',
    },
    auth: { signIn: 'Log in', signUp: 'Start for free', toApp: 'Open app' },
    footer: {
      tagline:
        'Your personal letting assistant. Locari does the work – you decide.',
      newsletterAria: 'Subscribe to the newsletter',
      newsletterEmailLabel: 'Email address',
      newsletterPlaceholder: 'you@email.com',
      newsletterButton: 'Subscribe',
      newsletterNote: 'More info in our',
      newsletterSuccess:
        "Almost done! If you're not subscribed yet, a confirmation email is on its way.",
      privacyLabel: 'Privacy Policy',
      colProduct: 'Product',
      colCompany: 'Company',
      colLegal: 'Legal',
      colContact: 'Contact',
      funktionen: 'All features',
      whatsapp: 'Control via WhatsApp',
      ki: 'Artificial intelligence',
      sicherheit: 'Security',
      ueberUns: 'About',
      blog: 'Blog',
      changelog: 'Changelog',
      karriere: 'Careers',
      presse: 'Press',
      trustCenter: 'Trust Center',
      impressum: 'Imprint',
      cookieRichtlinie: 'Cookie Policy',
      cookieSettings: 'Cookie settings',
      widerruf: 'Withdrawal & cancellation',
      dsaMeldung: 'Report illegal content',
      kontaktAufnehmen: 'Get in touch',
      supportHilfe: 'Support & help',
      datenschutzKontakt: 'Data protection',
      statusLabel: 'System status',
      socialAria: 'Locari on social media',
      copyrightSuffix: 'Locari. A service by Ametis Digital GmbH.',
      addressLine: 'Rathausgasse 17 · 12529 Schönefeld · Germany',
      langAria: 'Choose language',
    },
  },
  fr: {
    brandAria: 'Accueil Locari',
    openMenu: 'Ouvrir le menu',
    nav: {
      service: 'Comment ça marche',
      warumLocari: 'Pourquoi Locari',
      antiProfiling: 'Anti-profilage',
      whatsapp: 'WhatsApp',
      preise: 'Tarifs',
      faq: 'FAQ',
    },
    auth: {
      signIn: 'Se connecter',
      signUp: 'Commencer gratuitement',
      toApp: 'Vers la plateforme',
    },
    footer: {
      tagline:
        'Ton assistant de location personnel. Locari travaille – tu décides.',
      newsletterAria: 'S’abonner à la newsletter',
      newsletterEmailLabel: 'Adresse e-mail',
      newsletterPlaceholder: 'toi@email.com',
      newsletterButton: 'S’abonner',
      newsletterNote: "Plus d'infos dans notre",
      newsletterSuccess:
        "Presque terminé ! Si tu n'es pas encore inscrit, un e-mail de confirmation est en route.",
      privacyLabel: 'Politique de confidentialité',
      colProduct: 'Produit',
      colCompany: 'Entreprise',
      colLegal: 'Mentions légales',
      colContact: 'Contact',
      funktionen: 'Toutes les fonctionnalités',
      whatsapp: 'Pilotage par WhatsApp',
      ki: 'Intelligence artificielle',
      sicherheit: 'Sécurité',
      ueberUns: 'À propos',
      blog: 'Blog',
      changelog: 'Changelog',
      karriere: 'Carrières',
      presse: 'Presse',
      trustCenter: 'Trust Center',
      impressum: 'Informations légales',
      cookieRichtlinie: 'Politique cookies',
      cookieSettings: 'Paramètres cookies',
      widerruf: 'Rétractation & résiliation',
      dsaMeldung: 'Signaler un contenu illicite',
      kontaktAufnehmen: 'Nous contacter',
      supportHilfe: 'Support & aide',
      datenschutzKontakt: 'Protection des données',
      statusLabel: 'État du système',
      socialAria: 'Locari sur les réseaux sociaux',
      copyrightSuffix: 'Locari. Un service d’Ametis Digital GmbH.',
      addressLine: 'Rathausgasse 17 · 12529 Schönefeld · Allemagne',
      langAria: 'Choisir la langue',
    },
  },
};
